// Qué llevar a un viaje.
//
// La pregunta que contesta no es "qué me pongo" sino "qué meto en el
// bolso para no llevar de más ni quedarme corto".
//
// DECISIONES DE PANTALLA
//
// 1. Lo primero que se ve es la LISTA PARA EMPACAR, no el plan día por
//    día. Parado frente al bolso, lo que necesitás es la lista; el
//    plan es la justificación de por qué esa lista alcanza, y va
//    abajo para quien quiera verificarla.
//
// 2. Cada prenda dice EN CUÁNTOS DÍAS SE USA. Es lo que hace creíble
//    la lista: "este pantalón lo usás 4 de 5 días" explica por qué
//    está adentro mejor que cualquier otra cosa.
//
// 3. Se muestra el ahorro contra llevar un conjunto por día. Sin ese
//    número, "12 prendas" no dice si está bien o mal.

import { useCallback, useEffect, useRef, useState } from "react";
import * as api from "./api";
import { cargarOpciones } from "./opciones";
import { useAuth } from "./AuthContext";
import Visor from "./Visor";
import ElegirPrendas from "./ElegirPrendas";
import { fotoChica, fotoGrande } from "./foto";
import { t, tCat, tPlural, useIdioma, tError, numero } from "./i18n";

/* ------------------------------------------------------------------
   LA VALIJA SE ACUERDA DE LO QUE MARCASTE

   Pedido del dueño (2026-08-21). Antes, cambiar de pestaña para mirar
   una prenda y volver borraba todo: las ocasiones, los días, la
   estación, el tope de calzados. Y armar una valija no es una tarea de
   un minuto — se piensa a lo largo del día, se corrige, se vuelve.

   Es el mismo principio que ya está escrito en este proyecto:
   PERDER EL LUGAR ES LO QUE HACE QUE UNO NO SE MOLESTE EN CORREGIR UN
   DATO. Acá el lugar es el viaje entero.

   EN EL NAVEGADOR **Y** EN LA CUENTA — corregido el 2026-08-23.

   Acá decía que el servidor no hacía falta, con este argumento: un
   borrador de valija es de este momento y de esta persona sentada acá,
   y guardarlo obligaría a decidir cosas que nadie pidió (¿cuántos
   borradores?, ¿cuándo se vencen?).

   El argumento resolvía el problema chico —no perder el lugar al
   cambiar de pestaña— y daba por inexistentes los grandes: la valija se
   perdía al limpiar el navegador y NO EXISTÍA EN EL TELÉFONO. Que es,
   justamente, el aparato que uno tiene en la mano parado frente al
   bolso.

   Las preguntas que "nadie pidió" tenían respuesta y era corta: UNA
   valija por usuario, la última, sin vencimiento (decisión del dueño,
   2026-08-23). Con eso la tabla es una fila y la PK es el user_id.

   AHORA HAY DOS CAPAS Y CADA UNA HACE UNA COSA:

     localStorage — instantánea. Escribe sin latencia, sobrevive a un
     servidor caído, y es lo que se pinta en el primer render para que
     la pantalla no aparezca vacía y después se llene.

     `valija_usuario` — durable. Sobrevive al navegador y viaja al
     teléfono.

   CUANDO LAS DOS TIENEN ALGO, MANDA EL SERVIDOR: es la única que pudo
   haber sido escrita desde otro aparato. Y hasta que la lectura del
   servidor no termina, NO SE ESCRIBE NADA en él — si no, el estado
   local de esta pestaña pisaría lo que guardaste desde el teléfono
   hace cinco minutos, que es la carrera clásica de este patrón.

   SE GUARDA TAMBIÉN EL RESULTADO — revertido el 2026-08-21.

   Acá decía que se guardaba solo lo MARCADO y no el resultado, con
   este argumento: el armario pudo cambiar —una prenda al lavadero, otra
   marcada como gastada— y mostrar una valija vieja como si siguiera
   siendo válida sería peor que no mostrar nada.

   El argumento es correcto y la conclusión estaba mal. Lo que pasa en
   la práctica es más simple: mirás una prenda de la lista, tocás la
   foto, vas al armario a chequear algo, volvés — y la valija entera
   desapareció. Hay que volver a armarla para seguir en el mismo lugar.

   Es el principio que ya está escrito en este proyecto: PERDER EL LUGAR
   ES LO QUE HACE QUE UNO NO SE MOLESTE EN CORREGIR UN DATO. Se aplicaba
   a las ocasiones marcadas y no al resultado, que es justamente lo que
   uno está mirando.

   El riesgo del armario desactualizado se paga con lo que ya existe:
   cambiar cualquier filtro limpia el resultado, y "Armar valija" está
   siempre ahí. Una valija de hace diez minutos con una prenda que
   mandaste al lavadero es un problema chico y visible; perder el
   trabajo cada vez que cambiás de pestaña es un problema grande e
   invisible.
------------------------------------------------------------------ */

const CLAVE_BORRADOR = "brujel.valija.borrador";

function leerBorrador() {
  try {
    const crudo = localStorage.getItem(CLAVE_BORRADOR);
    if (!crudo) return null;
    const b = JSON.parse(crudo);
    // Se valida la FORMA, no solo que exista. Un borrador de una
    // versión vieja de la app —o tocado a mano— rompería la pantalla
    // al arrancar, que es el peor momento posible para romperse.
    if (!b || typeof b !== "object") return null;
    if (b.ocasiones && typeof b.ocasiones !== "object") return null;
    // El resultado guardado tiene que TENER la forma que la pantalla
    // espera. Un borrador de una versión vieja de la app, o tocado a
    // mano, rompería el render al arrancar — el peor momento posible.
    if (b.resultado && !Array.isArray(b.resultado.prendas)) {
      delete b.resultado;
    }
    if (b.elegidas && !Array.isArray(b.elegidas)) delete b.elegidas;
    return b;
  } catch {
    return null;
  }
}

function guardarBorrador(b) {
  try {
    localStorage.setItem(CLAVE_BORRADOR, JSON.stringify(b));
  } catch {
    // Modo privado, cuota llena, lo que sea. Guardar el borrador es una
    // comodidad; que la valija no se pueda armar, no.
  }
}

function olvidarBorrador() {
  try {
    localStorage.removeItem(CLAVE_BORRADOR);
  } catch {
    // Ídem.
  }
}

export default function Valija() {
  // Re-renderiza si cambia el idioma: `tCat` se resuelve en el render.
  useIdioma();
  const [opciones, setOpciones] = useState(null);
  // Se lee UNA vez, al montar, con la forma de inicialización perezosa
  // de useState. Con `useState(leerBorrador()?.ocasiones || {})` a
  // secas, `leerBorrador` correría en cada render.
  const [ocasiones, setOcasiones] = useState(
    () => leerBorrador()?.ocasiones || {}
  );
  // La estación del viaje, no el clima. Es lo que decide qué ropa entra
  // al bolso: nadie empaca lino para un viaje en invierno.
  //
  // Arranca en la del país del usuario, pero acá se cambia más seguido
  // que en "Qué me pongo" — justamente se viaja a otro clima.
  const { estacionActual } = useAuth();
  const [estacion, setEstacion] = useState(() => leerBorrador()?.estacion || null);

  useEffect(() => {
    if (estacionActual) setEstacion((a) => a || estacionActual);
  }, [estacionActual]);
  const [maxCalzados, setMaxCalzados] = useState(
    () => leerBorrador()?.maxCalzados || 2
  );
  // Las prendas que decidiste llevar sí o sí. Van al borrador como todo
  // lo demás: elegir diez prendas y perderlas al cambiar de pestaña
  // sería peor que no tener la función.
  const [elegidas, setElegidas] = useState(
    () => leerBorrador()?.elegidas || []
  );
  const [eligiendo, setEligiendo] = useState(false);
  // El resultado también se recuerda. Las fotos vienen firmadas y las
  // firmas vencen —por eso `guardarBorrador` no puede guardar cualquier
  // cosa para siempre— pero duran horas, y una valija se arma en
  // minutos. Si una foto se rompe, se ve el hueco de "sin foto" y
  // rearmar es un botón.
  const [resultado, setResultado] = useState(() => leerBorrador()?.resultado || null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [ampliada, setAmpliada] = useState(null);
  const [verPlan, setVerPlan] = useState(false);

  // `null` mientras se lee la valija de la cuenta. Hasta que deje de
  // serlo NO se escribe en el servidor: ver el comentario de arriba
  // sobre la carrera con otro dispositivo.
  const [sincronizado, setSincronizado] = useState(false);
  // Lo último que se mandó al servidor, serializado. Sirve para no
  // repetir un PUT idéntico —el primero, apenas se carga, sería
  // exactamente eso— y para no gastar una llamada por cada render.
  const ultimoEnviado = useRef(null);

  useEffect(() => {
    cargarOpciones().then(setOpciones);
  }, []);

  // Leer la valija de la cuenta, UNA vez al montar.
  //
  // Si el servidor tiene algo, pisa lo local: es la única copia que
  // pudo haber sido escrita desde otro aparato. Si no tiene nada
  // —primera vez, o "Nueva valija" desde otro lado— se deja lo local,
  // que es lo que el usuario está viendo en pantalla ahora mismo.
  useEffect(() => {
    let vivo = true;

    api
      .leerValijaGuardada()
      .then(({ valija }) => {
        if (!vivo || !valija) return;
        const hayAlgo =
          Object.keys(valija.ocasiones || {}).length > 0 ||
          (valija.elegidas || []).length > 0 ||
          !!valija.resultado;
        if (!hayAlgo) return;

        setOcasiones(valija.ocasiones || {});
        setEstacion(valija.estacion || null);
        setMaxCalzados(valija.maxCalzados ?? 2);
        setElegidas(valija.elegidas || []);
        setResultado(valija.resultado || null);
      })
      .catch((e) => {
        // No se rompe la pantalla: con el borrador local la valija
        // funciona igual. Pero tampoco se calla, porque el error más
        // probable es "falta correr valija_guardada.sql" y eso tiene
        // arreglo — esconderlo manda a buscar el problema a otro lado.
        if (vivo)
          setError(
            t("valija.errorLeer", "No se pudo leer la valija guardada: {mensaje}", {
              mensaje: tError(e),
            })
          );
      })
      .finally(() => {
        if (vivo) setSincronizado(true);
      });

    return () => {
      vivo = false;
    };
  }, []);

  // Guardar en cada cambio, sin botón de "guardar".
  //
  // Un borrador que hay que acordarse de guardar no es un borrador: es
  // otra cosa que se puede perder.
  //
  // El navegador se escribe SIEMPRE y al instante: cuesta nada y es lo
  // que salva el cambio de pestaña.
  useEffect(() => {
    guardarBorrador({ ocasiones, estacion, maxCalzados, elegidas, resultado });
  }, [ocasiones, estacion, maxCalzados, elegidas, resultado]);

  // El servidor se escribe con freno de 800 ms.
  //
  // Sin el freno, subir los días de "Trabajo" de 0 a 5 dispara cinco
  // PUT con el mismo JSON de 40 KB. Con el freno, uno.
  useEffect(() => {
    if (!sincronizado) return;

    // Una valija vacía no se guarda. Si se guardara, "Nueva valija"
    // borraría la fila y 800 ms después la volvería a crear vacía —y el
    // usuario que abre desde el teléfono se encontraría con una valija
    // en blanco en vez de con nada, que no es lo mismo.
    const vacia =
      Object.keys(ocasiones).length === 0 &&
      elegidas.length === 0 &&
      !resultado;
    if (vacia) return;

    const cuerpo = { ocasiones, estacion, maxCalzados, elegidas, resultado };
    const firma = JSON.stringify(cuerpo);
    // El primer disparo después de leer del servidor mandaría de vuelta
    // exactamente lo que acaba de llegar. Se saltea.
    if (firma === ultimoEnviado.current) return;

    const id = setTimeout(() => {
      ultimoEnviado.current = firma;
      // Si falla, no se le avisa al usuario ni se reintenta: el borrador
      // local ya lo tiene y el próximo cambio vuelve a intentar. Un
      // cartel de error por cada toque a un botón sería peor que el
      // problema.
      api.guardarValija(cuerpo).catch(() => {
        ultimoEnviado.current = null;
      });
    }, 800);

    return () => clearTimeout(id);
  }, [sincronizado, ocasiones, estacion, maxCalzados, elegidas, resultado]);

  // Empezar de cero. Vuelve a la estación del país, que es de donde
  // habría arrancado la primera vez.
  const nuevaValija = useCallback(() => {
    olvidarBorrador();
    // Y también en la cuenta: si solo se borrara el navegador, la
    // próxima apertura leería el servidor y la valija "borrada"
    // volvería sola. Es el bug que hace que nadie confíe en un botón de
    // borrar.
    ultimoEnviado.current = null;
    /* ⚠️  EL `catch(() => {})` ERA EL BUG QUE EL COMENTARIO DE ARRIBA
     *     DICE EVITAR (BA-11, 2026-09-05).
     *
     * Si el DELETE remoto falla —sin red, sesión vencida, 500— el
     * navegador queda limpio y el servidor NO. La próxima apertura lee
     * el servidor y la valija "borrada" vuelve sola, que es
     * literalmente lo que las tres líneas de arriba se proponen
     * impedir. Y el usuario no se enteraba de nada: el botón se
     * comportaba como si hubiera funcionado.
     *
     * Ahora se avisa. La pantalla local igual se limpia —lo que pediste
     * pasó de tu lado— pero decir que quedó a medias es la diferencia
     * entre "no anda" y "no confío en el botón de borrar". */
    api.olvidarValija().catch((e) => {
      setError(
        t(
          "valija.noSeBorroEnLaCuenta",
          "Se borró acá, pero no en tu cuenta: puede volver a aparecer en otro dispositivo. Probá de nuevo. ({motivo})",
          { motivo: tError(e) }
        )
      );
    });
    setOcasiones({});
    setEstacion(estacionActual || null);
    setMaxCalzados(2);
    setElegidas([]);
    setEligiendo(false);
    setResultado(null);
    setError("");
    setVerPlan(false);
  }, [estacionActual]);

  const hayAlgoMarcado =
    Object.keys(ocasiones).length > 0 ||
    maxCalzados !== 2 ||
    elegidas.length > 0 ||
    !!resultado;

  const totalDias = Object.values(ocasiones).reduce((a, b) => a + b, 0);

  function sumar(ocasion, delta) {
    setOcasiones((o) => {
      const n = Math.max(0, (o[ocasion] || 0) + delta);
      const nuevo = { ...o };
      if (n === 0) delete nuevo[ocasion];
      else nuevo[ocasion] = n;
      return nuevo;
    });
    setResultado(null);
  }

  async function armar() {
    if (totalDias === 0) return;
    setCargando(true);
    setError("");
    try {
      setResultado(
        await api.armarValija({
          ocasiones,
          estacion,
          maxCalzados,
          prendas: elegidas,
        })
      );
    } catch (e) {
      setError(tError(e));
    } finally {
      setCargando(false);
    }
  }

  if (!opciones) return <p className="tenue">{t("comun.cargando", "Cargando...")}</p>;

  return (
    <section>
      <div className="viaje">
        <p className="filtro-titulo">{t("valija.cuantosDias", "¿Cuántos días de cada cosa?")}</p>

        {/* Contadores y no un formulario de fechas: para armar la
            valija da igual si el día de trabajo es el martes o el
            jueves. Pedir fechas sería pedir precisión que no se usa. */}
        <div className="dias-lista">
          {opciones.categorias.map((c) => (
            <div key={c} className={`dia-fila ${ocasiones[c] ? "dia-activa" : ""}`}>
              <span className="dia-nombre">{tCat("ocasion", c)}</span>
              <div className="dia-contador">
                <button
                  className="dia-boton"
                  onClick={() => sumar(c, -1)}
                  disabled={!ocasiones[c]}
                  aria-label={t("valija.unDiaMenos", "Un día menos de {ocasion}", {
                    ocasion: tCat("ocasion", c),
                  })}
                >
                  −
                </button>
                <span className="dia-numero">{numero(ocasiones[c] || 0)}</span>
                <button
                  className="dia-boton"
                  onClick={() => sumar(c, 1)}
                  aria-label={t("valija.unDiaMas", "Un día más de {ocasion}", {
                    ocasion: tCat("ocasion", c),
                  })}
                >
                  +
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="viaje-ajustes">
          <label className="selector">
            <span className="selector-etiqueta">{t("valija.estacion", "Estación")}</span>
            <select
              value={estacion || ""}
              onChange={(e) => {
                setEstacion(e.target.value || null);
                setResultado(null);
              }}
            >
              {/* Sin estación se empaca del armario entero. Es lo
                  correcto para un viaje que cruza climas, y la única
                  respuesta honesta cuando no sabemos adónde va. */}
              <option value="">{t("valija.cualquiera", "Cualquiera")}</option>
              {(opciones.estaciones || []).map((e2) => (
                <option key={e2} value={e2}>
                  {tCat("estacion", e2)}
                  {e2 === estacionActual ? " " + t("valija.acaHoy", "(acá, hoy)") : ""}
                </option>
              ))}
            </select>
          </label>

          <label className="selector">
            <span className="selector-etiqueta">{t("valija.calzados", "Calzados")}</span>
            <select
              value={maxCalzados}
              onChange={(e) => {
                setMaxCalzados(Number(e.target.value));
                setResultado(null);
              }}
            >
              {[1, 2, 3, 4].map((n) => (
                <option key={n} value={n}>
                  {t("valija.hasta", "hasta {n}", { n })}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* Elegir prendas va DESPUÉS de los días y ANTES de armar.
            Ese orden importa: primero decidís a qué vas, y con eso en la
            cabeza te acordás de qué querés ponerte. Al revés —elegir
            ropa sin saber los días— es elegir a ciegas.

            Va plegado porque es opcional: la valija sigue funcionando
            sin tocar nada acá, y desplegado empujaría el botón principal
            fuera de la pantalla todos los días. */}
        <button
          type="button"
          className="boton-texto valija-elegir"
          onClick={() => setEligiendo((v) => !v)}
        >
          {eligiendo
            ? t("valija.listo", "Listo")
            : elegidas.length > 0
            ? t("valija.teLlevas", "Te llevas {n} sí o sí — cambiar", { n: elegidas.length })
            : t("valija.quieresLlevar", "¿Hay algo que te quieres llevar sí o sí?")}
        </button>

        {eligiendo && (
          <ElegirPrendas
            elegidas={elegidas}
            onCambiar={(nuevas) => {
              setElegidas(nuevas);
              setResultado(null);
            }}
          />
        )}

        <button
          className="boton-primario"
          onClick={armar}
          disabled={totalDias === 0 || cargando}
        >
          {cargando
            ? t("valija.armando", "Armando...")
            : totalDias === 0
            ? t("valija.sumaUnDia", "Suma al menos un día")
            : tPlural("plural.diasValija", totalDias)}
        </button>

        {/* "Nueva valija" solo aparece si hay algo que borrar.
            Un botón de descartar siempre visible al lado de la acción
            principal invita a tocarlo por error, y el error acá es
            perder el viaje que estabas armando. */}
        {hayAlgoMarcado && (
          <button className="boton-texto valija-nueva" onClick={nuevaValija}>
            {t("valija.nueva", "Nueva valija (empezar de cero)")}
          </button>
        )}
      </div>

      {error && <div className="mensaje-error" role="alert">{error}</div>}

      {resultado && (
        <>
          {/* LOS AVISOS DE LO QUE ELEGISTE VAN ARRIBA DE TODO.
              Antes que el resumen, antes que la lista.

              Son la función, no un adorno: sin ellos la app completa o
              descarta en silencio, y un silencio en una valija se
              descubre en el hotel. El más importante es "esta prenda no
              entra en ningún día" — nadie más te lo va a decir, porque
              la valija te la mete igual por haberla elegido vos. */}
          {(resultado.avisos || []).map((a) => (
            <div key={a.tipo} className="aviso">
              {a.texto}
            </div>
          ))}

          {/* El ahorro va arriba de todo. Sin ese número, "12 prendas"
              no dice si el resultado es bueno. */}
          <div className="valija-resumen">
            <div>
              <strong className="valija-total">{resultado.total}</strong>
              <span className="tenue"> {t("valija.prendasPara", "prendas para {dias} días", { dias: resultado.dias })}</span>
            </div>
            {resultado.sinOptimizar > resultado.total && (
              <p className="texto-mini tenue" style={{ margin: 0 }}>
                {t("valija.ahorro", "Un conjunto distinto por día serían {sinOptimizar}. Te ahorras {ahorro}.", {
                  sinOptimizar: resultado.sinOptimizar,
                  ahorro: resultado.sinOptimizar - resultado.total,
                })}
              </p>
            )}
          </div>

          {resultado.sinResolver.length > 0 && (
            <div className="aviso">
              <p style={{ margin: "0 0 4px" }}>
                <strong>
                  {tPlural("plural.diasSinResolver", resultado.sinResolver.length)}
                </strong>
              </p>
              {resultado.sinResolver.map((d) => (
                <p key={d.indice} className="texto-mini" style={{ margin: 0 }}>
                  {tCat("ocasion", d.ocasion)}: {d.motivo}
                </p>
              ))}
            </div>
          )}

          <p className="filtro-titulo">{t("valija.paraMeter", "Para meter en el bolso")}</p>
          <div className="grilla-prendas">
            {resultado.prendas.map((p) => (
              <article
                key={p.id}
                className={`prenda ${p.elegida ? "prenda-elegida" : ""}`}
              >
                {fotoChica(p) ? (
                  <img loading="lazy" decoding="async"
                    src={fotoChica(p)}
                    alt={p.nombre}
                    className="prenda-foto"
                    onClick={() =>
                      setAmpliada({ url: fotoGrande(p), alt: p.nombre, prenda: p })
                    }
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      /* Enter y espacio hacen lo mismo que el click, sea lo que
                         sea. No se duplica la expresión del onClick: se dispara
                         el click de verdad. Copiar el handler exigía parsear JSX
                         con llaves anidadas, que es justamente lo que este
                         proyecto ya aprendió a no hacer. */
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        e.currentTarget.click();
                      }
                    }}
                  />
                ) : (
                  <div className="prenda-foto prenda-sin-foto">
                    <span className="tenue texto-mini">{t("valija.sinFoto", "Sin foto")}</span>
                  </div>
                )}
                <div className="prenda-cuerpo">
                  <p className="prenda-nombre">{p.nombre}</p>
                  {/* El número que justifica que esta prenda esté en la
                      lista. Es lo que convierte una lista en un
                      argumento. */}
                  <p className="texto-mini valija-dias">
                    {t("valija.usoDias", "{dias} de {total} días", { dias: p.dias, total: resultado.dias })}
                    {/* Distinguir lo tuyo de lo que sugirió la app.
                        Importa cuando el número dice "0 de 5": si la
                        eligió la app, es un bug; si la elegiste vos, es
                        una decisión tuya que la app respetó. */}
                    {p.elegida && <span className="marca-elegida"> · {t("valija.laElegiste", "la elegiste tú")}</span>}
                  </p>
                </div>
              </article>
            ))}
          </div>

          <button
            className="boton-secundario valija-ver-plan"
            onClick={() => setVerPlan((v) => !v)}
          >
            {verPlan
              ? t("valija.ocultarPlan", "Ocultar el plan")
              : t("valija.verPlan", "Ver día por día")}
          </button>

          {verPlan && (
            <div className="plan">
              {resultado.plan.map((d) => (
                <div key={d.indice} className="plan-dia">
                  <div className="plan-cabecera">
                    <strong>{t("valija.dia", "Día {n}", { n: d.indice + 1 })}</strong>
                    <span className="tenue texto-mini"> · {tCat("ocasion", d.ocasion)}</span>
                  </div>
                  {d.prendas ? (
                    <div className="plan-fotos">
                      {d.prendas.map((p) =>
                        fotoChica(p) ? (
                          <img loading="lazy" decoding="async"
                            key={p.id}
                            src={fotoChica(p)}
                            alt={p.nombre}
                            title={p.nombre}
                            onClick={() =>
                              setAmpliada({ url: fotoGrande(p), alt: p.nombre, prenda: p })
                            }
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                              /* Enter y espacio hacen lo mismo que el click, sea lo que
                                 sea. No se duplica la expresión del onClick: se dispara
                                 el click de verdad. Copiar el handler exigía parsear JSX
                                 con llaves anidadas, que es justamente lo que este
                                 proyecto ya aprendió a no hacer. */
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                e.currentTarget.click();
                              }
                            }}
                          />
                        ) : (
                          <span key={p.id} className="plan-hueco" title={p.nombre} />
                        )
                      )}
                    </div>
                  ) : (
                    <p className="texto-mini aviso-dudosa">{t("valija.sinResolver", "Sin resolver")}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <Visor
        url={ampliada?.url}
        alt={ampliada?.alt}
        prenda={ampliada?.prenda}
        onCerrar={() => setAmpliada(null)}
      />
    </section>
  );
}
